import { v4 as uuid } from "uuid";
import draggable from "vuedraggable";
import FormFieldDialog from "./FormFieldDialog/FormFieldDialog.vue";
import FormPublisher from "./FormPublisher/FormPublisher.vue";
import FormExportImport from "./FormExportImport/FormExportImport.vue";
import { getTokenHeader } from "@/helpers/commonHelper";
import { confirmWarningConfig } from "@/configuration/confirmConfigurations";

const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);

const AVAILABLE_FIELDS = [
  { type: "text", label: "Textfeld", icon: "mdi-format-text" },
  { type: "email", label: "E-Mail", icon: "mdi-email" },
  { type: "phone", label: "Telefon", icon: "mdi-phone" },
  { type: "url", label: "URL", icon: "mdi-link-variant" },
  { type: "number", label: "Zahlfeld", icon: "mdi-numeric" },
  { type: "textarea", label: "Textbereich", icon: "mdi-format-paragraph" },
  { type: "select", label: "Dropdown", icon: "mdi-menu-down" },
  { type: "checkbox", label: "Checkbox", icon: "mdi-checkbox-marked" },
  {
    type: "checkbox-image",
    label: "Checkbox mit Bildern",
    icon: "mdi-image-multiple"
  },
  { type: "radio", label: "Radio Button", icon: "mdi-radiobox-marked" },
  { type: "date", label: "Datum", icon: "mdi-calendar" },
  { type: "file", label: "Dateiupload", icon: "mdi-file-upload" },
  { type: "image", label: "Bild-Upload", icon: "mdi-image-plus" },
  { type: "section", label: "Bereich", icon: "mdi-format-section" },
  { type: "html", label: "HTML", icon: "mdi-language-html5" },
  { type: "link", label: "Link", icon: "mdi-link" },
  { type: "submit", label: "Senden-Button", icon: "mdi-send" },
  { type: "hidden", label: "Verborgenes Feld", icon: "mdi-eye-off" }
];

const AVAILABLE_LAYOUTS = [
  {
    type: "two-column",
    label: "Zweispaltig",
    icon: "mdi-page-layout-body-columns"
  },
  { type: "three-column", label: "Dreispaltig", icon: "mdi-page-layout-body" },
  { type: "multi-step", label: "Mehrstufig", icon: "mdi-format-list-numbered" }
];

const LAYOUT_TYPES = AVAILABLE_LAYOUTS.map(l => l.type);

const DISALLOWED_DROP_TYPES_BY_ZONE = {
  "layout-column": LAYOUT_TYPES,
  step: ["multi-step"]
};

function getApiUrl() {
  return `${env.form_builder_api_client_url}api/forms`;
}

function createEmptyField(type) {
  const field = {
    id: uuid(),
    type,
    label: "",
    placeholder: "",
    required: false,
    dontSend: false,
    validation: "",
    helpText: "",
    options: []
  };
  if (type === "multi-step") {
    field.steps = [{ id: uuid(), title: "Schritt 1", fields: [] }];
    field.columns = undefined;
  } else if (LAYOUT_TYPES.includes(type)) {
    field.columns = type === "three-column" ? [[], [], []] : [[], []];
  }
  if (type === "number") {
    field.step = 1;
  }
  return field;
}

function ensureFormData(form) {
  if (!form.data) {
    form.data = { fields: [] };
  }
  if (!form.data.fields) {
    form.data.fields = [];
  }
}

function initializeLayouts(fields) {
  if (!fields) return;
  fields.forEach(field => {
    if (field.type === "two-column" && !field.columns) {
      field.columns = [[], []];
    } else if (field.type === "three-column" && !field.columns) {
      field.columns = [[], [], []];
    } else if (field.type === "multi-step") {
      if (
        !field.steps ||
        !Array.isArray(field.steps) ||
        field.steps.length === 0
      ) {
        field.steps = [{ id: uuid(), title: "Schritt 1", fields: [] }];
      }
      field.steps.forEach(step => {
        if (!step.id) step.id = uuid();
        if (!step.title) step.title = "Schritt";
        if (!step.fields) step.fields = [];
      });
    }
  });
}

function getDefaultFormProperties() {
  return {
    recipients: [],
    recipientsFreetext: "",
    cc: [],
    ccFreetext: "",
    submissionReaderGroupId: null,
    replyToEmail: "",
    senderName: "",
    nameFieldId: null,
    ccEmailFieldId: null,
    successMessage: "",
    redirectUrl: "",
    widgetThemeOverridesEnabled: false,
    widgetThemeOverrides: null
  };
}

function getDefaultDataProtectionProperties() {
  return {
    emailDispatchMode: "respectFieldSettings",
    autoDeleteEnabled: false,
    autoDeleteValue: 1,
    autoDeleteUnit: "weeks"
  };
}

const DEFAULT_WIDGET_THEME = {
  "color-accent": "#2468c9",
  "color-error": "#c55c52",
  "color-info": "#314d57",
  "color-primary": "#97bd0f",
  "color-secondary": "#5d7114",
  "color-text": "#333333",
  "color-warning": "#c55c52"
};

function normalizeHexColor(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const withHash = raw.startsWith("#") ? raw : `#${raw}`;
  const validHex = /^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(withHash);
  if (!validHex) return "";

  if (withHash.length === 4) {
    return `#${withHash[1]}${withHash[1]}${withHash[2]}${withHash[2]}${withHash[3]}${withHash[3]}`.toLowerCase();
  }

  return withHash.toLowerCase();
}

function getPrincipalWidgetThemeDefaults(principal) {
  const widgetColors = principal?.colorPalette?.forms?.widget?.colors;
  if (!Array.isArray(widgetColors)) {
    return { ...DEFAULT_WIDGET_THEME };
  }

  const mapped = widgetColors.reduce((acc, colorEntry) => {
    if (!colorEntry?.key || !colorEntry?.value) return acc;
    const normalized = normalizeHexColor(colorEntry.value);
    if (normalized) {
      acc[colorEntry.key] = normalized;
    }
    return acc;
  }, {});

  return {
    ...DEFAULT_WIDGET_THEME,
    ...mapped
  };
}

function resolveWidgetThemeForProperties(properties, principal) {
  const defaults = getPrincipalWidgetThemeDefaults(principal);
  const useOverrides = properties?.widgetThemeOverridesEnabled === true;
  if (!useOverrides) {
    return defaults;
  }

  const overrideSource = properties?.widgetThemeOverrides;
  const normalizedOverrides = {};
  if (overrideSource && typeof overrideSource === "object") {
    Object.keys(DEFAULT_WIDGET_THEME).forEach(key => {
      const normalized = normalizeHexColor(overrideSource[key]);
      if (normalized) {
        normalizedOverrides[key] = normalized;
      }
    });
  }

  return {
    ...defaults,
    ...normalizedOverrides
  };
}

export default {
  name: "FormBuilder",
  components: { draggable, FormFieldDialog, FormPublisher, FormExportImport },

  data() {
    return {
      forms: [],
      selectedFormId: null,
      selectedFieldId: null,
      showFieldDialog: false,
      showPreview: false,
      showExportDialog: false,
      currentField: null,
      isLoading: false,
      isSaving: false,
      saveTimer: null,
      availableFields: AVAILABLE_FIELDS,
      availableLayouts: AVAILABLE_LAYOUTS,
      showFormPropertiesDialog: false,
      formProperties: getDefaultFormProperties(),
      formPropertiesFormId: null,
      showDataProtectionDialog: false,
      dataProtectionProperties: getDefaultDataProtectionProperties(),
      dataProtectionFormId: null,
      dataProtectionDurationOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      dataProtectionUnitOptions: [
        { text: "Woche(n)", value: "weeks" },
        { text: "Monate(n)", value: "months" }
      ],
      usersLoading: false,
      groupsLoading: false,
      initialSubmissionReaderGroupId: null
    };
  },

  computed: {
    selectedForm() {
      if (!this.selectedFormId) return null;
      const id = String(this.selectedFormId);
      return (
        this.forms.find(f => {
          return String(f._id || "") === id || String(f._tempId || "") === id;
        }) || null
      );
    },
    selectedFormFields() {
      if (!this.selectedForm) return [];
      ensureFormData(this.selectedForm);
      return this.selectedForm.data.fields;
    },
    principal_id() {
      return this.$store.state.selectedPrincipal?.id || "";
    },
    userEmailOptions() {
      const users = this.$store.state.users || [];
      return users
        .filter(
          u => u.email && u.principal_id === this.principal_id && u.active
        )
        .map(u => ({
          email: u.email,
          label: `${u.name} (${u.email})`
        }));
    },
    textFields() {
      return this.getAllFieldsByType("text");
    },
    emailFields() {
      return this.getAllFieldsByType("email");
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    isSuperAdmin() {
      return this.currentUser?.scopes?.includes("role:admin");
    },
    isPrincipalAdmin() {
      return (
        this.currentUser?.principal_id === this.principal_id &&
        !!this.currentUser?.additional_data?.eberl_admin
      );
    },
    canEditSubmissionReaderGroup() {
      return this.isSuperAdmin || this.isPrincipalAdmin;
    },
    submissionReaderGroupOptions() {
      const groups = this.$store.state.groups || [];
      return groups
        .filter(group => group?.principal_id === this.principal_id)
        .map(group => ({
          value: group.id || group._id,
          text: group.name
        }));
    }
  },

  async created() {
    await this.$store.dispatch("getCurrentUser");
    await this.$store.dispatch("getGroups");

    // Berechtigung prüfen
    const currentUser = this.$store.state.currentUser;
    const hasFormEditorAccess =
      currentUser?.scopes?.includes("role:form_editor") ||
      currentUser?.scopes?.includes("role:admin");
    if (!hasFormEditorAccess) {
      this.$router.push("/forms");
      return;
    }
  },

  async mounted() {
    await this.loadForms();
  },

  beforeDestroy() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveForm();
    }
  },

  methods: {
    buildFormPropertiesPayload(formId, sourceProperties = {}, options = {}) {
      const { respectGroupPermission = true } = options;
      const merged = {
        ...getDefaultFormProperties(),
        ...(sourceProperties || {})
      };

      return {
        formId,
        principal_id: this.principal_id,
        recipients: Array.isArray(merged.recipients) ? merged.recipients : [],
        recipientsFreetext: merged.recipientsFreetext || "",
        cc: Array.isArray(merged.cc) ? merged.cc : [],
        ccFreetext: merged.ccFreetext || "",
        submissionReaderGroupId: respectGroupPermission
          ? this.canEditSubmissionReaderGroup
            ? merged.submissionReaderGroupId || null
            : this.initialSubmissionReaderGroupId
          : merged.submissionReaderGroupId || null,
        replyToEmail: merged.replyToEmail || "",
        senderName: merged.senderName || "",
        nameFieldId: merged.nameFieldId || null,
        ccEmailFieldId: merged.ccEmailFieldId || null,
        successMessage: merged.successMessage || "",
        redirectUrl: merged.redirectUrl || "",
        widgetThemeOverridesEnabled:
          merged.widgetThemeOverridesEnabled === true,
        // Die wirksamen Widgetfarben immer persistieren (Mandant + ggf. Overrides).
        widgetThemeOverrides: resolveWidgetThemeForProperties(
          merged,
          this.$store.state.selectedPrincipal
        )
      };
    },

    async ensureFormPropertiesWithCurrentTheme(formId) {
      if (!this.principal_id || !formId) return;
      const headers = getTokenHeader();

      let existingProperties = getDefaultFormProperties();
      try {
        const {
          data
        } = await this.$axios.get(
          `${env.form_builder_api_client_url}api/form-properties/${this.principal_id}/${formId}`,
          { headers }
        );
        if (data?.data) {
          existingProperties = {
            ...existingProperties,
            ...data.data
          };
        }
      } catch (e) {
        // Kein bestehender Datensatz ist beim Neuanlegen ein valider Fall.
      }

      const payload = this.buildFormPropertiesPayload(
        formId,
        existingProperties,
        { respectGroupPermission: false }
      );

      await this.$axios.put(
        `${env.form_builder_api_client_url}api/form-properties/${this.principal_id}/${formId}`,
        payload,
        { headers }
      );
    },

    // API
    async loadForms() {
      if (!this.principal_id) return;
      try {
        this.isLoading = true;
        const headers = getTokenHeader();
        const { data } = await this.$axios.get(
          `${getApiUrl()}/${this.principal_id}/list`,
          { headers }
        );
        this.forms = data.data || [];
      } catch (err) {
        console.error("Fehler beim Laden:", err);
        this.$snackbar("Fehler beim Laden der Formulare", "error");
      } finally {
        this.isLoading = false;
      }
    },

    async loadFormById(formId) {
      if (!this.principal_id || !formId) return;
      try {
        const headers = getTokenHeader();
        const { data } = await this.$axios.get(
          `${getApiUrl()}/${this.principal_id}/${formId}`,
          { headers }
        );
        if (data?.data) {
          const idx = this.forms.findIndex(f => f._id === formId);
          if (idx !== -1) {
            this.$set(this.forms, idx, data.data);
          }
        }
      } catch (err) {
        console.error("Fehler beim Laden des Formulars:", err);
        this.$snackbar("Fehler beim Laden des Formulars", "error");
      }
    },

    debouncedSave() {
      if (this.saveTimer) clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => this.saveForm(), 1500);
    },

    async saveForm() {
      if (!this.selectedForm || !this.principal_id || this.isSaving) return;
      try {
        this.isSaving = true;
        const headers = getTokenHeader();
        const fields = this.selectedForm.data?.fields || [];

        const payload = {
          name: this.selectedForm.name,
          description: this.selectedForm.description || "",
          principal_id: this.principal_id,
          data: {
            fields: JSON.parse(JSON.stringify(fields))
          }
        };

        if (!this.selectedForm._id) {
          const { data } = await this.$axios.post(
            `${getApiUrl()}/${this.principal_id}`,
            payload,
            { headers }
          );
          if (data?.data) {
            await this.ensureFormPropertiesWithCurrentTheme(data.data._id);
            const tempId = this.selectedForm._tempId;
            const idx = this.forms.findIndex(f => f._tempId === tempId);
            if (idx !== -1) {
              initializeLayouts(data.data.data?.fields);
              this.$set(this.forms, idx, data.data);
              this.selectedFormId = data.data._id;
            }
          }
        } else {
          await this.$axios.put(
            `${getApiUrl()}/${this.principal_id}/${this.selectedForm._id}`,
            payload,
            { headers }
          );
        }
      } catch (err) {
        console.error("Fehler beim Speichern:", err);
        this.$snackbar("Fehler beim Speichern des Formulars", "error");
      } finally {
        this.isSaving = false;
      }
    },

    // ── Formular-Verwaltung ─────────────────────────────────────────
    createNewForm() {
      const tempId = uuid();
      const newForm = {
        _tempId: tempId,
        principal_id: this.principal_id,
        name: `Formular ${this.forms.length + 1}`,
        description: "",
        data: { fields: [] }
      };
      this.forms.unshift(newForm);
      this.$nextTick(() => {
        this.selectedFormId = tempId;
      });
    },

    createDemoForm() {
      const tempId = uuid();
      const demoFields = [
        {
          id: uuid(),
          type: "text",
          label: "Vorname",
          placeholder: "Max",
          required: true,
          validation: "",
          helpText: "Geben Sie Ihren Vornamen ein",
          options: []
        },
        {
          id: uuid(),
          type: "text",
          label: "Nachname",
          placeholder: "Mustermann",
          required: true,
          validation: "",
          helpText: "",
          options: []
        },
        {
          id: uuid(),
          type: "email",
          label: "E-Mail",
          placeholder: "max@example.com",
          required: true,
          validation: "",
          helpText: "Wir werden Sie per E-Mail kontaktieren",
          options: []
        },
        {
          id: uuid(),
          type: "phone",
          label: "Telefonnummer",
          placeholder: "+49 (0)123 456789",
          required: false,
          validation: "",
          helpText: "",
          options: []
        },
        {
          id: uuid(),
          type: "select",
          label: "Betreff",
          placeholder: "Wählen Sie einen Betreff",
          required: true,
          validation: "",
          helpText: "",
          options: [
            { id: uuid(), label: "Anfrage" },
            { id: uuid(), label: "Beschwerde" },
            { id: uuid(), label: "Lob" },
            { id: uuid(), label: "Sonstiges" }
          ]
        },
        {
          id: uuid(),
          type: "textarea",
          label: "Nachricht",
          placeholder: "Geben Sie Ihre Nachricht hier ein...",
          required: true,
          validation: "",
          helpText: "Bitte seien Sie ausführlich",
          options: []
        },
        {
          id: uuid(),
          type: "checkbox",
          label: "Ich akzeptiere die Bedingungen",
          placeholder: "",
          required: true,
          validation: "",
          helpText: "",
          options: [
            { id: uuid(), label: "Ich akzeptiere die Nutzungsbedingungen" }
          ]
        },
        {
          id: uuid(),
          type: "submit",
          label: "Formular absenden",
          placeholder: "",
          required: false,
          validation: "",
          helpText: "",
          options: []
        }
      ];

      const newForm = {
        _tempId: tempId,
        principal_id: this.principal_id,
        name: "Demo-Formular",
        description: "Ein Beispiel-Formular mit verschiedenen Feldtypen",
        data: { fields: demoFields }
      };

      this.forms.unshift(newForm);
      this.$nextTick(() => {
        this.selectedFormId = tempId;
      });
      this.$snackbar("Demo-Formular erstellt");
    },

    async duplicateForm(form) {
      if (!form) return;

      const copyNameBase = `${form.name || "Formular"} (Kopie)`;
      let copyName = copyNameBase;
      let counter = 2;
      const existingNames = (this.forms || []).map(item => item.name);
      while (existingNames.includes(copyName)) {
        copyName = `${copyNameBase} ${counter}`;
        counter++;
      }

      const duplicatedForm = {
        _tempId: uuid(),
        name: copyName,
        description: form.description || "",
        data: {
          fields: JSON.parse(JSON.stringify(form.data?.fields || []))
        }
      };

      this.forms.unshift(duplicatedForm);
      this.selectForm(duplicatedForm);

      await this.$nextTick();
      await this.saveForm();

      const sourceFormId = form._id || null;
      const targetFormId = this.selectedForm?._id || null;
      if (sourceFormId && targetFormId) {
        await this.copyFormProperties(sourceFormId, targetFormId);
      }

      this.$snackbar("Formular kopiert");
    },

    async copyFormProperties(sourceFormId, targetFormId) {
      if (!sourceFormId || !targetFormId || sourceFormId === targetFormId) {
        return;
      }

      try {
        const headers = getTokenHeader();
        const {
          data
        } = await this.$axios.get(
          `${env.form_builder_api_client_url}api/form-properties/${this.principal_id}/${sourceFormId}`,
          { headers }
        );

        if (!data?.data) {
          return;
        }

        const source = data.data;
        const payload = {
          formId: targetFormId,
          principal_id: this.principal_id,
          recipients: Array.isArray(source.recipients) ? source.recipients : [],
          recipientsFreetext: source.recipientsFreetext || "",
          cc: Array.isArray(source.cc) ? source.cc : [],
          ccFreetext: source.ccFreetext || "",
          submissionReaderGroupId: source.submissionReaderGroupId || null,
          replyToEmail: source.replyToEmail || "",
          senderName: source.senderName || "",
          nameFieldId: source.nameFieldId || null,
          ccEmailFieldId: source.ccEmailFieldId || null,
          successMessage: source.successMessage || "",
          redirectUrl: source.redirectUrl || "",
          widgetThemeOverridesEnabled:
            source.widgetThemeOverridesEnabled === true,
          widgetThemeOverrides:
            source.widgetThemeOverrides &&
            typeof source.widgetThemeOverrides === "object"
              ? source.widgetThemeOverrides
              : null
        };

        await this.$axios.put(
          `${env.form_builder_api_client_url}api/form-properties/${this.principal_id}/${targetFormId}`,
          payload,
          { headers }
        );
      } catch (e) {
        if (String(e).includes("404")) {
          return;
        }
        console.error("Fehler beim Kopieren der Formulareigenschaften:", e);
      }
    },

    async deleteForm(formId) {
      if (!formId) return;
      try {
        const form = this.forms.find(f => (f._id || f._tempId) === formId);
        const confirmed = await this.$confirm(
          `Sind Sie sicher, dass Sie das Formular<br>"${form?.name ||
            ""}" löschen wollen?`,
          confirmWarningConfig
        );
        if (!confirmed) return;
        if (form?._id) {
          const headers = getTokenHeader();
          await this.$axios.delete(
            `${getApiUrl()}/${this.principal_id}/${form._id}`,
            { headers }
          );
        }

        this.forms = this.forms.filter(f => (f._id || f._tempId) !== formId);
        if (this.selectedFormId === formId) {
          this.selectedFormId = null;
          this.selectedFieldId = null;
        }
        this.$snackbar("Formular gelöscht");
      } catch (err) {
        if (err?.message === "Dialog cancelled.") return;
        console.error("Fehler beim Löschen:", err);
        this.$snackbar("Fehler beim Löschen des Formulars", "error");
      }
    },

    selectForm(form) {
      this.selectedFormId = form._id || form._tempId;
      ensureFormData(form);
      initializeLayouts(form.data.fields);

      if (form._id && form.data.fields.length === 0) {
        this.loadFormById(form._id);
      }
    },

    // ── Drag & Drop ─────────────────────────────────────────────────
    getDraggableOptions(dropZone = "root") {
      const disallowedTypes = DISALLOWED_DROP_TYPES_BY_ZONE[dropZone];
      return {
        group: {
          name: "fields",
          pull: true,
          put: disallowedTypes
            ? function(to, from, dragEl) {
                var type = dragEl.getAttribute("data-field-type");
                if (!type) {
                  var child = dragEl.querySelector("[data-field-type]");
                  if (child) type = child.getAttribute("data-field-type");
                }
                return !(type && disallowedTypes.includes(type));
              }
            : true
        },
        animation: 150,
        ghostClass: "ghost",
        dragClass: "drag",
        emptyInsertThreshold: 80
      };
    },

    cloneField(item) {
      return createEmptyField(item.type);
    },

    updateForm() {
      if (this.selectedForm) this.debouncedSave();
    },

    // ── Feld-Aktionen ───────────────────────────────────────────────
    selectField(field) {
      this.selectedFieldId = field.id;
    },

    editField(field) {
      this.currentField = JSON.parse(JSON.stringify(field));
      this.showFieldDialog = true;
    },

    duplicateField(field, fieldsArray) {
      if (!fieldsArray) {
        fieldsArray = this.selectedForm?.data?.fields;
      }
      if (!fieldsArray || !field) return;

      // Feld tief kopieren
      const duplicatedField = JSON.parse(JSON.stringify(field));
      duplicatedField.id = uuid(); // Neue UUID vergeben

      // Index des Original-Felds finden
      const originalIndex = fieldsArray.findIndex(f => f.id === field.id);
      if (originalIndex !== -1) {
        // Duplikat unmittelbar nach dem Original einfügen
        fieldsArray.splice(originalIndex + 1, 0, duplicatedField);
      }

      this.debouncedSave();
      this.$snackbar("Element dupliziert");
    },

    saveField(updatedField) {
      if (!this.selectedForm?.data?.fields) return;

      const replaceFieldRecursively = fieldList => {
        if (!fieldList) return false;

        for (let i = 0; i < fieldList.length; i++) {
          const field = fieldList[i];
          if (field.id === updatedField.id) {
            this.$set(fieldList, i, updatedField);
            return true;
          }

          if (field.columns) {
            for (const col of field.columns) {
              if (replaceFieldRecursively(col)) return true;
            }
          }

          if (field.steps) {
            for (const step of field.steps) {
              if (replaceFieldRecursively(step.fields)) return true;
            }
          }
        }

        return false;
      };

      const found = replaceFieldRecursively(this.selectedForm.data.fields);

      if (found) this.debouncedSave();
      this.showFieldDialog = false;
      this.currentField = null;
    },

    async removeField(index, fieldsArray) {
      if (!fieldsArray) {
        fieldsArray = this.selectedForm?.data?.fields;
      }
      if (!fieldsArray) return;

      try {
        const confirmed = await this.$confirm(
          "Sind Sie sicher, dass Sie dieses Element löschen wollen?",
          confirmWarningConfig
        );
        if (!confirmed) return;
      } catch (e) {
        return;
      }

      fieldsArray.splice(index, 1);
      this.selectedFieldId = null;
      this.debouncedSave();
    },

    // ── Hilfsmethoden ───────────────────────────────────────────────
    getAllFieldsByType(type) {
      if (!this.selectedForm?.data?.fields) return [];
      const fields = [];

      const collectFields = fieldList => {
        if (!fieldList) return;
        for (const field of fieldList) {
          if (field.type === type && field.label) {
            fields.push({
              id: field.id,
              label: field.label
            });
          }
          // Suche in Layout-Spalten
          if (field.columns) {
            for (const col of field.columns) {
              collectFields(col);
            }
          }
          // Suche in Multi-Step-Schritten
          if (field.steps) {
            for (const step of field.steps) {
              collectFields(step.fields);
            }
          }
        }
      };

      collectFields(this.selectedForm.data.fields);
      return fields;
    },

    addStep(field) {
      if (!field.steps) {
        this.$set(field, "steps", []);
      }
      field.steps.push({
        id: uuid(),
        title: `Schritt ${field.steps.length + 1}`,
        fields: []
      });
      this.debouncedSave();
    },

    async removeStep(field, stepIndex) {
      if (!field.steps || field.steps.length <= 1) return;
      try {
        const confirmed = await this.$confirm(
          "Sind Sie sicher, dass Sie diesen Schritt löschen wollen?",
          confirmWarningConfig
        );
        if (!confirmed) return;
      } catch (e) {
        return;
      }
      field.steps.splice(stepIndex, 1);
      this.debouncedSave();
    },

    isLayout(fieldType) {
      return LAYOUT_TYPES.includes(fieldType);
    },

    getLayoutLabel(fieldType) {
      const layout = AVAILABLE_LAYOUTS.find(l => l.type === fieldType);
      return layout ? layout.label : fieldType;
    },

    getColumnCount(fieldType) {
      if (fieldType === "three-column") return 3;
      if (fieldType === "two-column") return 2;
      return 0;
    },

    getFieldIcon(fieldType) {
      const item =
        AVAILABLE_FIELDS.find(f => f.type === fieldType) ||
        AVAILABLE_LAYOUTS.find(l => l.type === fieldType);
      return item ? item.icon : "mdi-help-circle";
    },

    // ── Dialoge ─────────────────────────────────────────────────────
    previewForm() {
      if (this.selectedForm) this.showPreview = true;
    },

    onFormSubmit() {
      this.$snackbar("Formular erfolgreich eingereicht!");
    },

    exportForm() {
      if (this.selectedForm) this.showExportDialog = true;
    },

    async openFormProperties(form) {
      const formId = form._id || form._tempId;
      this.formPropertiesFormId = formId;
      this.formProperties = getDefaultFormProperties();
      this.initialSubmissionReaderGroupId = null;
      this.showFormPropertiesDialog = true;
      this.usersLoading = true;
      this.groupsLoading = true;
      try {
        await this.$store.dispatch("getCurrentUser");
        await Promise.all([
          this.$store.dispatch("getAllUsers"),
          this.$store.dispatch("getGroups")
        ]);
        await this.loadFormProperties(formId);
      } catch (e) {
        console.error("Fehler beim Laden der Formulareigenschaften:", e);
      } finally {
        this.usersLoading = false;
        this.groupsLoading = false;
      }
    },

    async loadFormProperties(formId) {
      try {
        const headers = getTokenHeader();
        const {
          data
        } = await this.$axios.get(
          `${env.form_builder_api_client_url}api/form-properties/${this.principal_id}/${formId}`,
          { headers }
        );
        if (data?.data) {
          this.formProperties = {
            ...getDefaultFormProperties(),
            recipients: data.data.recipients || [],
            recipientsFreetext: data.data.recipientsFreetext || "",
            cc: data.data.cc || [],
            ccFreetext: data.data.ccFreetext || "",
            submissionReaderGroupId: data.data.submissionReaderGroupId || null,
            replyToEmail: data.data.replyToEmail || "",
            senderName: data.data.senderName || "",
            nameFieldId: data.data.nameFieldId || null,
            ccEmailFieldId: data.data.ccEmailFieldId || null,
            successMessage: data.data.successMessage || "",
            redirectUrl: data.data.redirectUrl || "",
            widgetThemeOverridesEnabled:
              data.data.widgetThemeOverridesEnabled === true,
            widgetThemeOverrides:
              data.data.widgetThemeOverrides &&
              typeof data.data.widgetThemeOverrides === "object"
                ? data.data.widgetThemeOverrides
                : null
          };
          this.initialSubmissionReaderGroupId =
            this.formProperties.submissionReaderGroupId || null;
        }
      } catch (e) {
        // Keine Properties vorhanden – leer lassen
        console.log("Keine Formulareigenschaften gefunden:", e.message);
      }
    },

    async saveFormProperties() {
      if (!this.formPropertiesFormId) return;
      try {
        const headers = getTokenHeader();
        const payload = this.buildFormPropertiesPayload(
          this.formPropertiesFormId,
          this.formProperties
        );
        await this.$axios.put(
          `${env.form_builder_api_client_url}api/form-properties/${this.principal_id}/${this.formPropertiesFormId}`,
          payload,
          { headers }
        );
        this.$snackbar("Formulareigenschaften gespeichert");
        this.showFormPropertiesDialog = false;
      } catch (e) {
        console.error("Fehler beim Speichern:", e);
        this.$snackbar("Fehler beim Speichern der Eigenschaften", "error");
      }
    },

    async openDataProtectionDialog(form) {
      const formId = form._id || form._tempId;
      this.dataProtectionFormId = formId;
      this.dataProtectionProperties = getDefaultDataProtectionProperties();
      this.showDataProtectionDialog = true;
      await this.loadDataProtectionProperties(formId);
    },

    async loadDataProtectionProperties(formId) {
      if (!this.principal_id || !formId) return;
      try {
        const headers = getTokenHeader();
        const {
          data
        } = await this.$axios.get(
          `${env.form_builder_api_client_url}api/form-privacy/${this.principal_id}/${formId}`,
          { headers }
        );

        if (data?.data) {
          this.dataProtectionProperties = {
            ...getDefaultDataProtectionProperties(),
            ...data.data
          };
        }
      } catch (e) {
        if (String(e).includes("404")) {
          this.dataProtectionProperties = getDefaultDataProtectionProperties();
          return;
        }
        console.error("Fehler beim Laden der Datenschutzeinstellungen:", e);
        this.$snackbar(
          "Fehler beim Laden der Datenschutzeinstellungen",
          "error"
        );
      }
    },

    async saveDataProtectionProperties() {
      if (!this.dataProtectionFormId) return;
      if (!this.principal_id) return;

      const payload = {
        formId: this.dataProtectionFormId,
        principal_id: this.principal_id,
        ...getDefaultDataProtectionProperties(),
        ...this.dataProtectionProperties
      };

      try {
        const headers = getTokenHeader();
        await this.$axios.put(
          `${env.form_builder_api_client_url}api/form-privacy/${this.principal_id}/${this.dataProtectionFormId}`,
          payload,
          { headers }
        );
        this.showDataProtectionDialog = false;
        this.$snackbar("Datenschutzeinstellungen gespeichert");
      } catch (e) {
        console.error("Fehler beim Speichern der Datenschutzeinstellungen:", e);
        this.$snackbar(
          "Fehler beim Speichern der Datenschutzeinstellungen",
          "error"
        );
      }
    }
  }
};
